// #3 tester (2026-10-21) — the dock wall card's Go to post. Figma: section "Go to post — 2026-10-21" 4300:9759 on page
// "Wall sheet items — 2026-10-20" (Shared / Wall / Post highlight 4300:9760; Web / Wall / Go to post / Post
// highlighted 4300:10044, dark 4300:10212).
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { WallPost, WallRef } from "../../api/content";
import { sameWall, useWallJumpStore } from "../../store/wallJumpStore";

/** Page on until the post shows, at most this many loaded pages (the 100-post cap); then the post dialog. */
export const JUMP_MAX_PAGES = 5;
export const HIGHLIGHT_HOLD_MS = 1600;
export const HIGHLIGHT_FADE_MS = 400;
/** No list showing the post's wall took the jump by then (e.g. the GC is not in this street chat): the post dialog. */
export const JUMP_UNCLAIMED_MS = 6000;

/** Where a post's wall is: its GC street chat with the Channels (wall) panel, or the owner's personal page. */
export const wallRouteOf = (p: Pick<WallPost, "ownerType" | "ownerId">): string =>
  p.ownerType === "GC" ? `/street-chat?conv=${p.ownerId}&panel=CHANNELS` : `/profile/${p.ownerId}`;

/**
 * The dock's Go to post (call once, in StreamDock: it also owns the fallback). Opens the post's wall and leaves a
 * pending jump for its list; when the list cannot show the post, or no list takes the jump in time, the post opens
 * as a dialog over the page you are on. Playback is untouched.
 */
export function useGoToWallPost(): (post: WallPost) => void {
  const navigate = useNavigate();
  const location = useLocation();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pending = useRef<number | null>(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  // The jump ended any other way (shown, cancelled, replaced, sign-out reset): its 6 s timer goes too.
  useEffect(
    () =>
      useWallJumpStore.subscribe((s) => {
        if (pending.current != null && s.target?.nonce !== pending.current) {
          clearTimeout(timer.current);
          pending.current = null;
        }
      }),
    [],
  );

  const fallback = useWallJumpStore((s) => (s.target?.fallback ? s.target : null));
  useEffect(() => {
    if (!fallback) return;
    useWallJumpStore.getState().done(fallback.nonce);
    navigate(`/post/${fallback.post.id}`, { state: { backgroundLocation: location, post: fallback.post } });
  }, [fallback, navigate, location]);

  // The user left the wall's page before a list took the jump: cancel it, no late dialog elsewhere. Only once the
  // navigation to it has landed, and only the path counts: the street chat rewrites its own ?conv= (a GC not in this
  // street chat), which keeps the 6 s fallback.
  const unclaimed = useWallJumpStore((s) => (s.target && !s.target.claimed ? s.target : null));
  const arrived = useRef<number | null>(null);
  useEffect(() => {
    if (!unclaimed) return;
    const onWallPage = location.pathname === wallRouteOf(unclaimed.post).split("?")[0];
    if (onWallPage) arrived.current = unclaimed.nonce;
    else if (arrived.current === unclaimed.nonce) useWallJumpStore.getState().done(unclaimed.nonce);
  }, [unclaimed, location.pathname]);

  return useCallback(
    (post: WallPost) => {
      // A Blog post has a page of its own: land on it, no wall list to highlight it in.
      if ((post as { type?: string }).type === "BLOG") {
        navigate(`/post/${post.id}`, { state: { post } });
        return;
      }
      const nonce = useWallJumpStore.getState().request(post);
      pending.current = nonce;
      navigate(wallRouteOf(post));
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        if (useWallJumpStore.getState().target?.claimed === false) useWallJumpStore.getState().fallBack(nonce);
      }, JUMP_UNCLAIMED_MS);
    },
    [navigate],
  );
}

/** What useWallJump reads of a wall feed (useWallFeed's infinite query). */
export interface JumpFeed {
  isPending: boolean;
  isSuccess: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  pageCount: number;
  fetchNextPage(): unknown;
}

export const jumpFeedOf = (feed: Omit<JumpFeed, "pageCount"> & { data?: { pages: unknown[] } }): JumpFeed => ({
  isPending: feed.isPending,
  isSuccess: feed.isSuccess,
  isFetchingNextPage: feed.isFetchingNextPage,
  hasNextPage: feed.hasNextPage,
  pageCount: feed.data?.pages.length ?? 0,
  fetchNextPage: feed.fetchNextPage,
});

interface Options {
  /** The wall this list shows; null = not a wall (search results, Following, Liked). */
  wall: WallRef | null;
  feed: JumpFeed;
  posts: WallPost[];
  /** Holds the cards (`wall-post-<id>`); taps and scrolls in it clear the highlight. */
  rootRef: RefObject<HTMLElement | null>;
  /** Something covers the list (a composer): open the post as a dialog instead. */
  blocked?: boolean;
  /** The list is about to show (e.g. the comments column is closing): claim the jump, act once it is back. */
  waiting?: boolean;
}

/** A wall list's side of Go to post: scroll the post to the top and highlight it, paging on if needed. */
export function useWallJump({ wall, feed, posts, rootRef, blocked = false, waiting = false }: Options): { highlightOf(postId: string): "on" | "fading" | null } {
  const target = useWallJumpStore((s) => s.target);
  const [hl, setHl] = useState<{ postId: string; fading: boolean } | null>(null);
  const mine = target != null && !target.fallback && wall != null && sameWall(target.wall, wall) ? target : null;
  const found = mine != null && posts.some((p) => p.id === mine.post.id);
  const { isPending, isSuccess, isFetchingNextPage, hasNextPage, pageCount, fetchNextPage } = feed;

  const nonce = mine?.nonce ?? null;
  const jumpPost = mine?.post ?? null;
  // One next-page request per loaded page count, so a re-render before the fetch starts does not ask twice.
  const askedAt = useRef<string | null>(null);

  // Held while this list shows the jump's wall: unmounting mid-paging (or while the comments close) ends it.
  useEffect(() => (nonce == null ? undefined : useWallJumpStore.getState().hold(nonce)), [nonce]);

  useEffect(() => {
    if (nonce == null || !jumpPost) return;
    const store = useWallJumpStore.getState();
    store.claim(nonce);
    if (blocked) {
      store.fallBack(nonce);
      return;
    }
    if (waiting || isPending || isFetchingNextPage) return;
    if (found) {
      rootRef.current?.querySelector(`[data-wall-post="${jumpPost.id}"]`)?.scrollIntoView?.({ block: "start" });
      setHl({ postId: jumpPost.id, fading: false });
      store.done(nonce);
    } else if (isSuccess && hasNextPage && pageCount < JUMP_MAX_PAGES) {
      const ask = `${nonce}:${pageCount}`;
      if (askedAt.current === ask) return;
      askedAt.current = ask;
      void fetchNextPage();
    } else {
      store.fallBack(nonce);
    }
  }, [nonce, jumpPost, found, blocked, waiting, isPending, isSuccess, isFetchingNextPage, hasNextPage, pageCount, fetchNextPage, rootRef]);

  // Full strength, then a fade, then gone.
  useEffect(() => {
    if (!hl) return;
    const id = setTimeout(() => setHl((h) => (h && !h.fading ? { ...h, fading: true } : null)), hl.fading ? HIGHLIGHT_FADE_MS : HIGHLIGHT_HOLD_MS);
    return () => clearTimeout(id);
  }, [hl]);

  // The user scrolls or taps in the feed: cleared early (the jump's own scrollIntoView sends none of these).
  const lit = hl != null;
  useEffect(() => {
    const root = rootRef.current;
    if (!lit || !root) return;
    const clear = () => setHl(null);
    const events = ["pointerdown", "wheel", "touchmove", "keydown"] as const;
    for (const e of events) root.addEventListener(e, clear, { passive: true });
    return () => {
      for (const e of events) root.removeEventListener(e, clear);
    };
  }, [lit, rootRef]);

  return { highlightOf: (postId) => (hl?.postId === postId ? (hl.fading ? "fading" : "on") : null) };
}
